import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Undo2, Check, X, Clock, Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import { api } from '../../services/api';
import { useProfile } from '../../contexts/ProfileContext';
import { evaluateFilterExpression, type FilterCell, type AtomEvaluator } from '../../utils/columnFilter';
import { FilterPresets } from '../ui/filter-presets';
import { cleanFilters, type FilterSettings } from '../../utils/filterPresets';

interface PredictionData {
  Date: string;
  n_train: number;
  n_val: number;
  train_acc: number;
  val_acc: number;
  probability: number;
  Tomorrow_Prediction: string;
  Result: string;
  Tomorrow_Actual_Change: number | null;
}

const TICKERS_ALL = ['DSSA', 'AADI', 'BRMS', 'BRPT', 'DEWA', 'EMAS', 'ENRG', 'HRTA', 'INDY', 'RAJA', 'SMRA', 'SSIA', 'TPIA'];
const TICKERS_PUBLIC = ['DSSA'];
const PRIVILEGED_EMAILS = ['nathandaud123@gmail.com', 'nathandaud@student.ub.ac.id'];

type SortKey = keyof PredictionData;

const COLUMNS: SortKey[] = ['Date', 'n_train', 'n_val', 'train_acc', 'val_acc', 'probability', 'Tomorrow_Prediction', 'Tomorrow_Actual_Change', 'Result'];
const COLUMN_LABELS: Record<string, string> = {
  Date: 'Date', n_train: 'N_Train', n_val: 'N_Val', train_acc: 'Train Acc', val_acc: 'Val Acc',
  probability: 'Probability', Tomorrow_Prediction: 'Prediction', Tomorrow_Actual_Change: 'Actual Change', Result: 'Result',
};
// Filter keys outside the table would hide every row (processedData reads r[key]), so a preset only brings these back
const isColumn = (k: unknown): k is SortKey => typeof k === 'string' && (COLUMNS as string[]).includes(k);

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useEffect(() => {
    const timeout = setTimeout(() => { onChangeRef.current(value); }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return <input {...props} value={value} onChange={e => setValue(e.target.value)} />;
}

export const StoryForeignFlow = () => {
  const { profile } = useProfile();
  const isPrivileged =
    profile?.role === 'admin' ||
    (PRIVILEGED_EMAILS.includes(profile?.email || ''));
  const TICKERS = isPrivileged ? TICKERS_ALL : TICKERS_PUBLIC;

  const [selectedStock, setSelectedStock] = useState<string>('DSSA');
  const [activeStock, setActiveStock] = useState<string>('DSSA');
  const [data, setData] = useState<PredictionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'Date',
    direction: 'desc',
  });
  
  const [columnFilters, setColumnFilters] = useState<Partial<Record<SortKey, string>>>({});

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const tokenRes = await api.getBrokerSummarySasToken();
        if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
        const { containerUrl, sasToken } = tokenRes.data;
        const isDev = (import.meta as any).env?.DEV || false;
        
        const blobPath = `money_flow/ai/${activeStock}.csv`;
        const blobUrl = isDev
          ? `/azure-blob-proxy/trading-data-v2/${blobPath}?${sasToken}`
          : `${containerUrl}/${blobPath}?${sasToken}`;
          
        const response = await fetch(blobUrl);
        if (!response.ok) {
          if (response.status === 404) {
            setData([]);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedData = results.data
              .filter((row: any) => row.Date)
              .map((row: any) => ({
                Date: String(row.Date),
                n_train: Number(row.n_train) || 0,
                n_val: Number(row.n_val) || 0,
                train_acc: Number(row.train_acc) || 0,
                val_acc: Number(row.val_acc) || 0,
                probability: Number(row.probability) || 0,
                Tomorrow_Prediction: String(row.Tomorrow_Prediction),
                Result: String(row.Result),
                Tomorrow_Actual_Change: row.Tomorrow_Actual_Change === '' || row.Tomorrow_Actual_Change === null || row.Tomorrow_Actual_Change === undefined || String(row.Tomorrow_Actual_Change).toLowerCase() === 'nan' ? null : Number(row.Tomorrow_Actual_Change)
              }));
            setData(parsedData);
          }
        });
      } catch (e) {
        console.error("Failed to fetch AI Prediction data:", e);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [activeStock]);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const SortIndicator = ({ col }: { col: SortKey }) => {
    if (sortConfig.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  // Warna sel persis seperti yang dirender
  const getCellColorClass = (row: PredictionData, col: SortKey): string => {
    switch (col) {
      case 'Date': {
        const isLatest = !data.some(d => d.Date > row.Date);
        return isLatest ? 'text-yellow-400' : 'text-gray-300';
      }
      case 'Tomorrow_Prediction':
        return row.Tomorrow_Prediction === 'UP' ? 'text-[#00ff00]' : 'text-[#ff0000]';
      case 'Tomorrow_Actual_Change':
        return row.Tomorrow_Actual_Change === null ? 'text-gray-400'
          : row.Tomorrow_Actual_Change > 0 ? 'text-[#00ff00]'
          : row.Tomorrow_Actual_Change < 0 ? 'text-[#ff0000]'
          : 'text-white';
      case 'Result':
        return row.Result.includes('TRUE') ? 'text-[#00ff00]'
          : row.Result.includes('FALSE') ? 'text-[#ff0000]'
          : 'text-yellow-400';
      case 'n_train':
      case 'n_val':
        return 'text-gray-400';
      default:
        return 'text-white';
    }
  };

  const processedData = useMemo(() => {
    let result = [...data];

    // Filter ekspresi: token warna + kondisi numerik + teks, digabung and/or
    Object.entries(columnFilters).forEach(([key, filterValue]) => {
      if (!filterValue || !filterValue.trim()) return;
      const col = key as SortKey;
      result = result.filter(r => {
        const cellValue = r[col];
        if (cellValue === null || cellValue === undefined) return false;

        const isNumericCol = col !== 'Date' && col !== 'Tomorrow_Prediction' && col !== 'Result';
        const cell: FilterCell = {
          value: isNumericCol && typeof cellValue === 'number' ? cellValue : NaN,
          text: String(cellValue).toLowerCase(),
          colorClass: getCellColorClass(r, col),
          isNumeric: isNumericCol,
        };
        // Kolom Result: pemetaan lama ✅/❌/⏳ ↔ true/false/pending
        const customAtom: AtomEvaluator | undefined = col === 'Result'
          ? (atom) => {
              const a = atom.trim().toLowerCase();
              const v = String(cellValue).toLowerCase();
              if (a === '✅' || a.includes('true')) return v.includes('true');
              if (a === '❌' || a.includes('false')) return v.includes('false');
              if (a === '⏳' || a.includes('pending')) return v.includes('pending');
              return null;
            }
          : undefined;
        return evaluateFilterExpression(filterValue, cell, customAtom ? { customAtom } : undefined);
      });
    });

    // Sort
    result.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [data, columnFilters, sortConfig]);

  // Presets: filter row, sort (null = the default Date ↓) and the stock shown
  const presetCurrent = useMemo<FilterSettings>(() => ({
    filters: cleanFilters(columnFilters),
    sort: sortConfig.key === 'Date' && sortConfig.direction === 'desc' ? null : { key: sortConfig.key, direction: sortConfig.direction },
    view: { stock: activeStock },
  }), [columnFilters, sortConfig, activeStock]);

  const applyPreset = (s: FilterSettings) => {
    setColumnFilters(Object.fromEntries(Object.entries(cleanFilters(s.filters)).filter(([k]) => isColumn(k))) as Partial<Record<SortKey, string>>);
    const sort = s.sort;
    setSortConfig(sort && isColumn(sort.key) && (sort.direction === 'asc' || sort.direction === 'desc')
      ? { key: sort.key, direction: sort.direction }
      : { key: 'Date', direction: 'desc' });
    // Only a stock this account may pick (same list as the dropdown); a different one fetches like Show does
    const stock = s.view && typeof s.view === 'object' ? s.view['stock'] : undefined;
    if (typeof stock === 'string' && TICKERS.includes(stock)) {
      setSelectedStock(stock);
      setActiveStock(stock);
    }
  };

  const thClass = (col: SortKey) =>
    `px-2 py-1 text-center whitespace-nowrap cursor-pointer select-none hover:bg-white/10 transition-colors`;

  const formatDisplayDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] relative">
      {/* ── Top Control Bar ── */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-2 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap text-white">Stock:</label>
            <select
              value={selectedStock}
              onChange={(e) => setSelectedStock(e.target.value)}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm min-w-[120px] focus:outline-none focus:border-primary/50 transition-colors"
            >
              {TICKERS.map(ticker => (
                <option key={ticker} value={ticker}>{ticker}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            <span className="mr-2 px-2.5 py-1 text-[11px] font-bold tracking-wide bg-gradient-to-r from-green-500 to-emerald-400 text-black rounded-full shadow-[0_0_10px_rgba(34,197,94,0.3)] whitespace-nowrap">
              FREE TRIAL 30 HARI
            </span>
            <button
              onClick={() => setActiveStock(selectedStock)}
              className="h-8 px-4 bg-white text-black hover:bg-gray-200 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              Show
            </button>

            <button
              onClick={() => {
                setColumnFilters({});
                setSortConfig({ key: 'Date', direction: 'desc' });
              }}
              className="h-8 px-3 bg-transparent border border-white/20 text-white hover:bg-white/10 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5 rotate-180" />
              Reset
            </button>

            <FilterPresets page="foreign-flow" current={presetCurrent} onApply={applyPreset} label={k => COLUMN_LABELS[k] ?? k} />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full">
        <div className="flex-auto overflow-y-auto overflow-x-auto">
          <table className="w-full text-[13px] tabular-nums min-w-[900px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10">
              {/* Header Row */}
              <tr className="h-[28px] divide-x divide-white/30 border-b border-white/30">
                <th onClick={() => handleSort('Date')} className={thClass('Date')}>
                  Date<SortIndicator col="Date" />
                </th>
                <th onClick={() => handleSort('n_train')} className={thClass('n_train')}>
                  N_Train<SortIndicator col="n_train" />
                </th>
                <th onClick={() => handleSort('n_val')} className={thClass('n_val')}>
                  N_Val<SortIndicator col="n_val" />
                </th>
                <th onClick={() => handleSort('train_acc')} className={thClass('train_acc')}>
                  Train Acc<SortIndicator col="train_acc" />
                </th>
                <th onClick={() => handleSort('val_acc')} className={thClass('val_acc')}>
                  Val Acc<SortIndicator col="val_acc" />
                </th>
                <th onClick={() => handleSort('probability')} className={thClass('probability')}>
                  Probability<SortIndicator col="probability" />
                </th>
                <th onClick={() => handleSort('Tomorrow_Prediction')} className={thClass('Tomorrow_Prediction')}>
                  Prediction<SortIndicator col="Tomorrow_Prediction" />
                </th>
                <th onClick={() => handleSort('Tomorrow_Actual_Change')} className={thClass('Tomorrow_Actual_Change')}>
                  Actual Change<SortIndicator col="Tomorrow_Actual_Change" />
                </th>
                <th onClick={() => handleSort('Result')} className={thClass('Result')}>
                  Result<SortIndicator col="Result" />
                </th>
              </tr>

              {/* Filter Row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[24px] divide-x divide-white/30 border-b border-white/30">
                {COLUMNS.map((col) => (
                  <td key={col} className="px-1 py-0">
                    <DebouncedInput
                      type="text"
                      className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                      placeholder="Filter"
                      value={columnFilters[col] || ''}
                      onChange={(val) => setColumnFilters(prev => ({ ...prev, [col]: val }))}
                    />
                  </td>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-[#00ff00]" />
                      <span className="text-sm">Fetching from Azure Blob...</span>
                    </div>
                  </td>
                </tr>
              ) : processedData.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                processedData.map((row, idx) => {
                  const borderClass = '';
                  
                  const isUp = row.Tomorrow_Prediction === 'UP';
                  const predColor = isUp ? 'text-[#00ff00]' : 'text-[#ff0000]';
                  
                  const isTrue = row.Result.includes('TRUE');
                  const isFalse = row.Result.includes('FALSE');
                  const resultColor = isTrue ? 'text-[#00ff00]' : isFalse ? 'text-[#ff0000]' : 'text-yellow-400';

                  const actualChange = row.Tomorrow_Actual_Change;
                  const actualColor = actualChange === null ? 'text-gray-400' : actualChange > 0 ? 'text-[#00ff00]' : actualChange < 0 ? 'text-[#ff0000]' : 'text-white';

                  // Display date: show next row's date (the day the prediction is FOR),
                  // or 'Next Day' if this is the most recent row (sorted desc: idx=0 is latest)
                  const sortedDesc = sortConfig.direction === 'desc';
                  const isLatestRow = sortedDesc ? idx === 0 : idx === processedData.length - 1;
                  const nextRowDate = sortedDesc
                    ? (idx < processedData.length - 1 ? processedData[idx + 1].Date : null)
                    : (idx < processedData.length - 1 ? processedData[idx + 1].Date : null);
                  // Actually: for each row, the prediction is for the NEXT calendar day.
                  // The simplest approach: find the next row in original date order.
                  // Since processedData may be sorted asc or desc, find the next date from the sorted array.
                  const displayDate = isLatestRow
                    ? 'Next Day'
                    : formatDisplayDate(sortedDesc
                        ? processedData[idx + 1]?.Date   // next entry in desc = earlier date = but that's wrong
                        : processedData[idx + 1]?.Date); // next entry in asc = later date = correct

                  // Better: always use the chronologically next row's date.
                  // Sort data ascending by date to find next date.
                  const sortedAscDates = [...data].sort((a, b) => a.Date.localeCompare(b.Date)).map(d => d.Date);
                  const rowDateAscIdx = sortedAscDates.indexOf(row.Date);
                  const nextDateInAsc = rowDateAscIdx >= 0 && rowDateAscIdx < sortedAscDates.length - 1
                    ? sortedAscDates[rowDateAscIdx + 1]
                    : null;
                  const isLatest = nextDateInAsc === null;
                  const finalDisplayDate = isLatest ? 'Next Day' : formatDisplayDate(nextDateInAsc ?? undefined);

                  return (
                    <tr
                      key={idx}
                      className="hover:bg-white/[0.25] transition-colors h-[28px] divide-x divide-white/30"
                    >
                      <td className={`px-2 py-1 text-center ${isLatest ? 'text-yellow-400 font-semibold' : 'text-gray-300'} ${borderClass}`}>
                        {finalDisplayDate}
                      </td>
                      <td className={`px-2 py-1 text-center text-gray-400 ${borderClass}`}>
                        {row.n_train}
                      </td>
                      <td className={`px-2 py-1 text-center text-gray-400 ${borderClass}`}>
                        {row.n_val}
                      </td>
                      <td className={`px-2 py-1 text-center font-medium text-white ${borderClass}`}>
                        {row.train_acc.toFixed(2)}%
                      </td>
                      <td className={`px-2 py-1 text-center font-bold text-white ${borderClass}`}>
                        {row.val_acc.toFixed(2)}%
                      </td>
                      <td className={`px-2 py-1 text-center text-white ${borderClass}`}>
                        {row.probability.toFixed(2)}%
                      </td>
                      <td className={`px-2 py-1 text-center font-bold ${predColor} ${borderClass}`}>
                        {row.Tomorrow_Prediction === 'DOWN' ? 'DOWN/SW' : row.Tomorrow_Prediction}
                      </td>
                      <td className={`px-2 py-1 text-right font-medium ${actualColor} ${borderClass}`}>
                        {actualChange === null ? '-' : `${actualChange > 0 ? '+' : ''}${actualChange.toFixed(3)}%`}
                      </td>
                      <td className={`px-2 py-1 text-center font-medium ${borderClass}`}>
                        {isTrue ? <Check className="w-4 h-4 text-[#00ff00] mx-auto" strokeWidth={3} /> : isFalse ? <X className="w-4 h-4 text-[#ff0000] mx-auto" strokeWidth={3} /> : <Clock className="w-4 h-4 text-yellow-400 mx-auto" strokeWidth={3} />}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};